import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Button,
  Card,
  Input,
  Spinner,
  Tabs,
  TabsContent,
  TabsItem,
  TabsList,
  toast,
  Upload,
  UploadBody,
  UploadText,
} from 'keep-react'

import { Wrapper } from 'components'
import { FloppyDisk, Trash } from 'phosphor-react'
import { sliderService } from 'api/services/slider'
import { SliderFile, SliderItem } from 'api/types/slider'
import { Link } from 'react-router-dom'

export interface UploadFile {
  file: File
}

export interface SliderImages {
  slideID: number
  file: SliderFile | null
}

interface SliderFormValue {
  id: number | null
  title: string
  content: string
  link: string
}

interface SliderFormValues {
  slide1: SliderFormValue
  slide2: SliderFormValue
  slide3: SliderFormValue
  slide4: SliderFormValue
  slide5: SliderFormValue
  slide6: SliderFormValue
}

const createInitialSlide = (): SliderFormValue => ({
  id: null,
  title: '',
  content: '',
  link: '',
})

// form validation schema
const validationSchema = Yup.object().shape({
  categoryId: Yup.number(),
  status: Yup.number().oneOf([0, 1]),
  hasSimulation: Yup.boolean(),
  productFiles: Yup.mixed().nullable(),
})

export const BackofficeSliders = () => {
  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  // Save Button Active?
  const [hasChanges, setHasChanges] = useState<boolean>(false)

  // slide images state: initialize with 6 empty slots
  const initialSliderImages = Array.from({ length: 6 }, () => ({
    slideID: 0,
    file: null,
  }))
  const [sliderImages, setSliderImages] =
    useState<SliderImages[]>(initialSliderImages)

  const hasSlideContent = (slide: SliderFormValue) =>
    [slide.title, slide.content, slide.link].some(value => value.trim() !== '')

  const renderUploadArea = (
    slideId: number | null,
    handleDrop: (acceptedFiles: File[]) => void,
  ) => {
    if (!slideId) {
      return (
        <div className="rounded border border-dashed border-metal-200 px-4 py-6 text-body-4 text-metal-500">
          Grave o slide antes de enviar uma imagem.
        </div>
      )
    }

    return (
      <Upload
        horizontal
        options={{
          onDrop: handleDrop,
          multiple: false,
        }}
      >
        <UploadBody>
          <UploadText>
            <p className="text-body-4 font-normal text-metal-400">
              JPG or PNG formats
            </p>
          </UploadText>
        </UploadBody>
      </Upload>
    )
  }

  //get slider details
  const getSliderList = async () => {
    try {
      const response = (await sliderService.getSliderList()) as SliderItem[]

      // populate formik values and images array in a loop to avoid repetition
      const images: SliderImages[] = Array.from({ length: 6 }, () => ({
        slideID: 0,
        label: '',
        file: null as any,
      }))

      ;(response || []).slice(0, 6).forEach((slide, idx) => {
        if (!slide) return
        const prefix = `slide${idx + 1}`
        formik.setFieldValue(`${prefix}.id`, slide.id)
        formik.setFieldValue(`${prefix}.title`, slide.title || '')
        formik.setFieldValue(`${prefix}.content`, slide.content || '')
        formik.setFieldValue(`${prefix}.link`, slide.link || '')

        if (slide.files && slide.files.length > 0) {
          images[idx] = {
            slideID: slide.id,
            file: slide.files[0],
          }
        }
      })

      setSliderImages(images)
    } catch (error) {
      toast.error('Não foi possível carregar os sliders.')
    } finally {
      setIsLoading(false)
    }
  }

  // using formik to handle form state
  const formik = useFormik<SliderFormValues>({
    initialValues: {
      slide1: createInitialSlide(),
      slide2: createInitialSlide(),
      slide3: createInitialSlide(),
      slide4: createInitialSlide(),
      slide5: createInitialSlide(),
      slide6: createInitialSlide(),
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async () => {
      try {
        setIsApiBeingCalled(true)

        for (let i = 1; i < 7; i++) {
          const slide = formik.values[`slide${i}` as keyof SliderFormValues]
          const shouldSave = slide.id != null || hasSlideContent(slide)

          if (!shouldSave) {
            continue
          }

          try {
            await sliderService.saveSlider({
              id: slide.id,
              title: slide.title,
              content: slide.content,
              link: slide.link,
            })
          } catch (error) {
            toast.error(`Erro ao atualizar slide ${i}.`)
          }
        }

        await getSliderList()

        toast.success('Slider atualizado com sucesso.')
        setHasChanges(false)
      } catch (error) {
        toast.error('Erro ao atualizar slider.')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  const onDrop = async <T extends File>(
    acceptedFiles: T[],
    slideId?: number,
  ) => {
    // validate image type
    if (
      acceptedFiles[0].type !== 'image/jpeg' &&
      acceptedFiles[0].type !== 'image/png'
    ) {
      toast.error(
        'Formato de ficheiro inválido. Por favor, envie um ficheiro JPG ou PNG.',
      )
      return
    }

    try {
      setIsApiBeingCalled(true)

      // wait for the file upload to finish before reloading the list
      await sliderService.addSliderFiles({
        slideId: slideId || 0,
        file: acceptedFiles[0],
      })

      // refresh the list after upload completes so sliderImages is updated
      await getSliderList()

      toast.success('Imagem do slide atualizada com sucesso.')
    } catch (error) {
      toast.error('Erro ao atualizar imagem do slide.')
    } finally {
      setIsApiBeingCalled(false)
    }
  }

  const removeSliderImage = async (sliderIndex: number) => {
    try {
      setIsApiBeingCalled(true)

      const sliderId = sliderImages[sliderIndex].slideID
      const fileId = sliderImages[sliderIndex].file?.id
      if (!sliderId || !fileId) return
      await sliderService.removeSliderFile({
        sliderId: String(sliderId),
        fileId: String(fileId),
      })
      toast.success('Imagem do slide removida com sucesso.')
      getSliderList()
    } catch (error) {
      toast.error('Erro ao remover imagem do slide.')
    } finally {
      setIsApiBeingCalled(false)
    }
  }

  // detect form changes
  useEffect(() => {
    if (formik.values !== formik.initialValues) {
      setHasChanges(true)
    } else {
      setHasChanges(false)
    }
  }, [formik.values])

  // initial data fetch
  useEffect(() => {
    getSliderList()
  }, [])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        {isLoading ? (
          <Card className="w-full max-w-full p-4 md:p-6">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <h1 className="pb-10 text-xxxl font-medium text-primary-600 flex items-center justify-between">
              Sliders
              <Button
                type="submit"
                color="secondary"
                variant="default"
                disabled={!hasChanges || isApiBeingCalled}
                className="gap-2"
                onClick={() => {
                  formik.handleSubmit()
                }}
              >
                <FloppyDisk size={16} />
                Gravar alterações
              </Button>
            </h1>
            <Card className="w-full max-w-full p-4 md:p-6">
              <Tabs defaultValue="1">
                <TabsList>
                  <TabsItem value="1">Slide 1</TabsItem>
                  <TabsItem value="2">Slide 2</TabsItem>
                  <TabsItem value="3">Slide 3</TabsItem>
                  <TabsItem value="4">Slide 4</TabsItem>
                  <TabsItem value="5">Slide 5</TabsItem>
                  <TabsItem value="6">Slide Prime</TabsItem>
                </TabsList>

                <TabsContent value="1" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Nome
                      </h3>
                      <Input
                        name="title"
                        value={formik.values.slide1.title}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide1.title',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Descrição
                      </h3>
                      <Input
                        name="content"
                        value={formik.values.slide1.content}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide1.content',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Link
                      </h3>
                      <Input
                        name="link"
                        value={formik.values.slide1.link}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide1.link',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>

                    <div className="grid grid-cols-[1fr_4fr] gap-4 justify-between">
                      <div>
                        <h3 className="text-body-4 font-bold text-metal-900">
                          Imagem
                        </h3>
                        <p className="text-body-5 text-metal-300">
                          (Aspect Ratio 6:1)
                        </p>
                      </div>
                      {sliderImages[0].file ? (
                        <div className="grid grid-cols-4 items-center gap-2 relative">
                          <h4 className="font-semibold text-body-4">
                            Ficheiro
                          </h4>
                          <h4 className="font-semibold text-body-4">Tipo</h4>
                          <h4 className="font-semibold text-body-4">Tamanho</h4>
                          <h4 className="font-semibold text-body-4"></h4>
                          <div className="text-body-4">
                            <Link
                              to={sliderImages[0].file.uri}
                              target="_blank"
                              className="underline text-primary-500"
                            >
                              Ver Ficheiro
                            </Link>
                          </div>

                          <div className="text-body-4">
                            {sliderImages[0].file.contentType}
                          </div>

                          <div className="text-body-4">
                            {sliderImages[0].file.sizeBytes}
                          </div>
                          <div>
                            <Button
                              color="error"
                              variant="softBg"
                              shape="icon"
                              onClick={() => removeSliderImage(0)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        renderUploadArea(
                          formik.values.slide1.id,
                          acceptedFiles =>
                            onDrop(
                              acceptedFiles,
                              formik.values.slide1.id ?? undefined,
                            ),
                        )
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="2" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Nome
                      </h3>
                      <Input
                        name="title"
                        value={formik.values.slide2.title}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide2.title',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Descrição
                      </h3>
                      <Input
                        name="content"
                        value={formik.values.slide2.content}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide2.content',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Link
                      </h3>
                      <Input
                        name="link"
                        value={formik.values.slide2.link}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide2.link',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] gap-4 justify-between">
                      <div>
                        <h3 className="text-body-4 font-bold text-metal-900">
                          Imagem
                        </h3>
                        <p className="text-body-5 text-metal-300">
                          (Aspect Ratio 6:1)
                        </p>
                      </div>
                      {sliderImages[1].file ? (
                        <div className="grid grid-cols-4 items-center gap-2 relative">
                          <h4 className="font-semibold text-body-4">
                            Ficheiro
                          </h4>
                          <h4 className="font-semibold text-body-4">Tipo</h4>
                          <h4 className="font-semibold text-body-4">Tamanho</h4>
                          <h4 className="font-semibold text-body-4"></h4>
                          <div className="text-body-4">
                            <Link
                              to={sliderImages[1].file.uri}
                              target="_blank"
                              className="underline text-primary-500"
                            >
                              Ver Ficheiro
                            </Link>
                          </div>

                          <div className="text-body-4">
                            {sliderImages[1].file.contentType}
                          </div>

                          <div className="text-body-4">
                            {sliderImages[1].file.sizeBytes}
                          </div>
                          <div>
                            <Button
                              color="error"
                              variant="softBg"
                              shape="icon"
                              onClick={() => removeSliderImage(1)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        renderUploadArea(
                          formik.values.slide2.id,
                          acceptedFiles =>
                            onDrop(
                              acceptedFiles,
                              formik.values.slide2.id ?? undefined,
                            ),
                        )
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="3" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Nome
                      </h3>
                      <Input
                        name="title"
                        value={formik.values.slide3.title}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide3.title',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Descrição
                      </h3>
                      <Input
                        name="content"
                        value={formik.values.slide3.content}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide3.content',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Link
                      </h3>
                      <Input
                        name="link"
                        value={formik.values.slide3.link}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide3.link',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] gap-4 justify-between">
                      <div>
                        <h3 className="text-body-4 font-bold text-metal-900">
                          Imagem
                        </h3>
                        <p className="text-body-5 text-metal-300">
                          (Aspect Ratio 6:1)
                        </p>
                      </div>
                      {sliderImages[2].file ? (
                        <div className="grid grid-cols-4 items-center gap-2 relative">
                          <h4 className="font-semibold text-body-4">
                            Ficheiro
                          </h4>
                          <h4 className="font-semibold text-body-4">Tipo</h4>
                          <h4 className="font-semibold text-body-4">Tamanho</h4>
                          <h4 className="font-semibold text-body-4"></h4>
                          <div className="text-body-4">
                            <Link
                              to={sliderImages[2].file.uri}
                              target="_blank"
                              className="underline text-primary-500"
                            >
                              Ver Ficheiro
                            </Link>
                          </div>

                          <div className="text-body-4">
                            {sliderImages[2].file.contentType}
                          </div>

                          <div className="text-body-4">
                            {sliderImages[2].file.sizeBytes}
                          </div>
                          <div>
                            <Button
                              color="error"
                              variant="softBg"
                              shape="icon"
                              onClick={() => removeSliderImage(2)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        renderUploadArea(
                          formik.values.slide3.id,
                          acceptedFiles =>
                            onDrop(
                              acceptedFiles,
                              formik.values.slide3.id ?? undefined,
                            ),
                        )
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="4" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Nome
                      </h3>
                      <Input
                        name="title"
                        value={formik.values.slide4.title}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide4.title',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Descrição
                      </h3>
                      <Input
                        name="content"
                        value={formik.values.slide4.content}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide4.content',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Link
                      </h3>
                      <Input
                        name="link"
                        value={formik.values.slide4.link}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide4.link',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] gap-4 justify-between">
                      <div>
                        <h3 className="text-body-4 font-bold text-metal-900">
                          Imagem
                        </h3>
                        <p className="text-body-5 text-metal-300">
                          (Aspect Ratio 6:1)
                        </p>
                      </div>
                      {sliderImages[3].file ? (
                        <div className="grid grid-cols-4 items-center gap-2 relative">
                          <h4 className="font-semibold text-body-4">
                            Ficheiro
                          </h4>
                          <h4 className="font-semibold text-body-4">Tipo</h4>
                          <h4 className="font-semibold text-body-4">Tamanho</h4>
                          <h4 className="font-semibold text-body-4"></h4>
                          <div className="text-body-4">
                            <Link
                              to={sliderImages[3].file.uri}
                              target="_blank"
                              className="underline text-primary-500"
                            >
                              Ver Ficheiro
                            </Link>
                          </div>

                          <div className="text-body-4">
                            {sliderImages[3].file.contentType}
                          </div>

                          <div className="text-body-4">
                            {sliderImages[3].file.sizeBytes}
                          </div>
                          <div>
                            <Button
                              color="error"
                              variant="softBg"
                              shape="icon"
                              onClick={() => removeSliderImage(3)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        renderUploadArea(
                          formik.values.slide4.id,
                          acceptedFiles =>
                            onDrop(
                              acceptedFiles,
                              formik.values.slide4.id ?? undefined,
                            ),
                        )
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="5" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Nome
                      </h3>
                      <Input
                        name="title"
                        value={formik.values.slide5.title}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide5.title',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Descrição
                      </h3>
                      <Input
                        name="content"
                        value={formik.values.slide5.content}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide5.content',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Link
                      </h3>
                      <Input
                        name="link"
                        value={formik.values.slide5.link}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide5.link',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] gap-4 justify-between">
                      <div>
                        <h3 className="text-body-4 font-bold text-metal-900">
                          Imagem
                        </h3>
                        <p className="text-body-5 text-metal-300">
                          (Aspect Ratio 6:1)
                        </p>
                      </div>
                      {sliderImages[4].file ? (
                        <div className="grid grid-cols-4 items-center gap-2 relative">
                          <h4 className="font-semibold text-body-4">
                            Ficheiro
                          </h4>
                          <h4 className="font-semibold text-body-4">Tipo</h4>
                          <h4 className="font-semibold text-body-4">Tamanho</h4>
                          <h4 className="font-semibold text-body-4"></h4>
                          <div className="text-body-4">
                            <Link
                              to={sliderImages[4].file.uri}
                              target="_blank"
                              className="underline text-primary-500"
                            >
                              Ver Ficheiro
                            </Link>
                          </div>

                          <div className="text-body-4">
                            {sliderImages[4].file.contentType}
                          </div>

                          <div className="text-body-4">
                            {sliderImages[4].file.sizeBytes}
                          </div>
                          <div>
                            <Button
                              color="error"
                              variant="softBg"
                              shape="icon"
                              onClick={() => removeSliderImage(4)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        renderUploadArea(
                          formik.values.slide5.id,
                          acceptedFiles =>
                            onDrop(
                              acceptedFiles,
                              formik.values.slide5.id ?? undefined,
                            ),
                        )
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="6" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Nome
                      </h3>
                      <Input
                        name="title"
                        value={formik.values.slide6.title}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide6.title',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Descrição
                      </h3>
                      <Input
                        name="content"
                        value={formik.values.slide6.content}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide6.content',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Link
                      </h3>
                      <Input
                        name="link"
                        value={formik.values.slide6.link}
                        onChange={e => {
                          formik.setFieldValue(
                            'slide6.link',
                            e.currentTarget.value,
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] gap-4 justify-between">
                      <div>
                        <h3 className="text-body-4 font-bold text-metal-900">
                          Imagem
                        </h3>
                        <p className="text-body-5 text-metal-300">
                          (Aspect Ratio 6:1)
                        </p>
                      </div>
                      {sliderImages[5].file ? (
                        <div className="grid grid-cols-4 items-center gap-2 relative">
                          <h4 className="font-semibold text-body-4">
                            Ficheiro
                          </h4>
                          <h4 className="font-semibold text-body-4">Tipo</h4>
                          <h4 className="font-semibold text-body-4">Tamanho</h4>
                          <h4 className="font-semibold text-body-4"></h4>
                          <div className="text-body-4">
                            <Link
                              to={sliderImages[5].file.uri}
                              target="_blank"
                              className="underline text-primary-500"
                            >
                              Ver Ficheiro
                            </Link>
                          </div>

                          <div className="text-body-4">
                            {sliderImages[5].file.contentType}
                          </div>

                          <div className="text-body-4">
                            {sliderImages[5].file.sizeBytes}
                          </div>
                          <div>
                            <Button
                              color="error"
                              variant="softBg"
                              shape="icon"
                              onClick={() => removeSliderImage(5)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        renderUploadArea(
                          formik.values.slide6.id,
                          acceptedFiles =>
                            onDrop(
                              acceptedFiles,
                              formik.values.slide6.id ?? undefined,
                            ),
                        )
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </Card>
          </>
        )}
      </Wrapper>
    </div>
  )
}
